import { useCallback } from 'react';
import {
  Settings,
  ShipInfo,
  TargetInfoType,
  TargetTypeEnum,
} from '@ais-ui/websockets';
import { useSettings } from '../Settings/SettingsProvider';

interface AisReportAgeResult {
  aisReportAge: number;
  isAisReportOld: boolean;
}

interface AisReportAgeHook {
  getAisReportAge: (
    targetInfo: TargetInfoType,
    targetType: TargetTypeEnum
  ) => AisReportAgeResult;
}

function checkAisReportAge(
  lastAisReport: Date | undefined | null,
  warningTimeoutInMinutes: number | undefined
) {
  // Return null values if required data is missing
  if (!lastAisReport || !warningTimeoutInMinutes) {
    return {
      isAisReportOld: null,
      timeSinceLastAisReport: null,
    };
  }

  try {
    const currentTime = new Date();

    // Calculate the difference in milliseconds
    const timeDifferenceMs = currentTime.getTime() - lastAisReport.getTime();

    // Convert to seconds
    const timeSinceLastAisReport = timeDifferenceMs / 1000;

    // Check if the report is older than the warning timeout
    const isAisReportOld =
      timeSinceLastAisReport > warningTimeoutInMinutes * 60;

    return {
      isAisReportOld,
      timeSinceLastAisReport,
    };
  } catch (error) {
    console.error('Error checking AIS report age:', error);
    return {
      isAisReportOld: null,
      timeSinceLastAisReport: null,
    };
  }
}

const getNewestReport = (targetInfo: TargetInfoType, settings: Settings) => {
  const warningTimeoutInMinutesTerrestrial =
    settings['ribbonPlugin.settings']?.['target_timeouts']?.[0]?.['lost'];

  const warningTimeoutInMinutesSatellite =
    settings['ribbonPlugin.settings']?.['target_timeouts']?.[1]?.['lost'];

  const lastAisReport = (targetInfo as ShipInfo)?.lastAisReport;

  const lastAisSatReport = (targetInfo as ShipInfo)?.lastAisSatReport;

  if (!lastAisReport && !lastAisSatReport) {
    return { report: null, timeout: null };
  }

  if (!lastAisReport) {
    return {
      report: lastAisSatReport,
      timeout: warningTimeoutInMinutesSatellite,
    };
  }

  if (!lastAisSatReport) {
    return {
      report: lastAisReport,
      timeout: warningTimeoutInMinutesTerrestrial,
    };
  }

  // Both exist, compare timestamps to get the newest
  const aisTime = lastAisReport.getTime();
  const satTime = lastAisSatReport.getTime();

  return aisTime > satTime
    ? { report: lastAisReport, timeout: warningTimeoutInMinutesTerrestrial }
    : { report: lastAisSatReport, timeout: warningTimeoutInMinutesSatellite };
};

export const useAisReportAge = (): AisReportAgeHook => {
  const { settings } = useSettings();

  const getAisReportAge = useCallback(
    (
      targetInfo: TargetInfoType,
      targetType: TargetTypeEnum
    ): AisReportAgeResult => {
      // Return default values for non-ship targets or missing data
      if (!targetInfo || !targetType || targetType !== TargetTypeEnum.Ship) {
        return {
          aisReportAge: 0,
          isAisReportOld: false,
        };
      }

      const { report: newestReport, timeout: warningTimeoutInMinutes } =
        getNewestReport(targetInfo, settings);

      const { isAisReportOld, timeSinceLastAisReport } = checkAisReportAge(
        newestReport,
        warningTimeoutInMinutes
      );

      return {
        aisReportAge: timeSinceLastAisReport || 0,
        isAisReportOld: isAisReportOld || false,
      };
    },
    [settings]
  );

  return {
    getAisReportAge,
  };
};
